import {AuthProfile, GiftedWishInfo, Image, ParticipantInfo, Profile, SecretSantaGame, SecretSantaGameInfo, SecretSantaMessage, SecretSantaParticipant, User, UserSettings, Wish, WishInfo, Wishlist, WishlistInfo} from "@/api/types.ts";
import apiClient from "@/api/apiClient.ts";

export const getAuthUser = async (): Promise<User> => {
  const response = await apiClient.get<User>('api/v2/auth');
  return response.data;
};

export const getAuthProfile = async (): Promise<AuthProfile> => {
  const response = await apiClient.get<AuthProfile>('api/v2/auth/profile');
  const data = response.data;
  if (data.user.birthday) {
    const date = new Date(data.user.birthday);
    data.user.birthday = new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  }

  return data
};

export const updateUserSettings = async (userSettings: UserSettings): Promise<UserSettings> => {
  if (userSettings.birthday) {
    const newSettings = {...userSettings};
    const date = new Date();
    date.setUTCFullYear(userSettings.birthday.getFullYear());
    date.setUTCMonth(userSettings.birthday.getMonth());
    date.setUTCDate(userSettings.birthday.getDate());
    date.setUTCHours(0, 0, 0, 0);
    newSettings.birthday = date
    userSettings = newSettings;
  }
  const response = await apiClient.put<UserSettings>(`api/v2/auth/profile`, userSettings);
  const data = response.data;
  if (data.birthday) {
    const date = new Date(data.birthday);
    data.birthday = new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  }

  return data
};

export const getShortIdInfo = async (shortId: number): Promise<{
  wishlistId: string,
  userId: string,
  isOwner: boolean,
}> => {
  const response = await apiClient.get<{ wishlistId: string, userId: string, isOwner: boolean }>(`api/v2/short-id/wishlists/${shortId}`);
  return response.data;
};

export const getProfile = async (id: string): Promise<Profile> => {
  const response = await apiClient.get<Profile>(`api/v2/profile/${id}`);
  const data = response.data;
  if (data.user.birthday) {
    data.user.birthday = new Date(data.user.birthday);
  }

  return data
};

export const getProfileWishes = async (
  id: string,
  params?: {status?: "active" | "gifted"; sortBy?: "giftedAt"}
): Promise<Wish[]> => {
  const response = await apiClient.get<Wish[]>(`api/v2/profile/${id}/wishes`, {params});
  return response.data;
};

export const startSearchProfiles = async (): Promise<void> => {
  const response = await apiClient.post<void>(`api/v2/profile/search`);
  return response.data;
};

export const createWishlist = async (wishlistInfo: WishlistInfo): Promise<Wishlist> => {
  const response = await apiClient.post<Wishlist>(`api/wishlists/create`, wishlistInfo);
  return response.data;
};

export const updateWishlist = async (id: string, wishlistInfo: WishlistInfo): Promise<WishlistInfo> => {
  const response = await apiClient.put<WishlistInfo>(`api/v2/wishlists/${id}`, wishlistInfo);
  return response.data;
};

export const deleteWishlist = async (id: string): Promise<void> => {
  const response = await apiClient.delete<void>(`api/v2/wishlists/${id}`);
  return response.data;
};

export const getWishlist = async (wishlistId: string): Promise<Wishlist> => {
  const response = await apiClient.get<Wishlist>(`api/v2/wishlists/${wishlistId}`);
  return response.data;
};

export const subscribeWishlist = async (wishlistId: string): Promise<void> => {
  const response = await apiClient.post<void>(`api/wishlists/${wishlistId}/subscribe`);
  return response.data;
};

export const unsubscribeWishlist = async (wishlistId: string): Promise<void> => {
  const response = await apiClient.delete<void>(`api/wishlists/${wishlistId}/subscribe`);
  return response.data;
};

export const bookWish = async (wishlistId: string, wishId: string): Promise<void> => {
  const response = await apiClient.put<void>(`api/wishlists/${wishlistId}/items/${wishId}/book`);
  return response.data;
};

export const unbookWish = async (wishlistId: string, wishId: string): Promise<void> => {
  const response = await apiClient.delete<void>(`api/wishlists/${wishlistId}/items/${wishId}/book`);
  return response.data;
};

export const copyWish = async (wishlistId: string, wishId: string): Promise<void> => {
  const response = await apiClient.post<void>(`api/wishlists/${wishlistId}/wishes/${wishId}/copy`);
  return response.data;
};

export const applyIdea = async (ideaId: string): Promise<string> => {
  const response = await apiClient.post<string>(`api/wishlists/f82b7ade-8a67-413f-9b1e-ef0617547cd5/wishes/${ideaId}/copy?addToWishlists=true`);
  return response.data;
};

export const hideIdea = async (ideaId: string): Promise<void> => {
  const response = await apiClient.post<void>(`api/v2/auth/profile/hide-idea?ideaId=${ideaId}`);
  return response.data;
};

export const addWish = async (wishInfo: WishInfo): Promise<Wish> => {
  const response = await apiClient.post<Wish>(`api/wish/add`, wishInfo);
  return response.data;
};

export const updateWish = async (wish: Wish): Promise<Wish> => {
  const response = await apiClient.put<Wish>(`api/wish/${wish.id}`, wish);
  return response.data;
};

export const markWishGifted = async (wishId: string, giftedWishInfo: GiftedWishInfo): Promise<Wish> => {
  const response = await apiClient.put<Wish>(`api/wish/${wishId}/gifted`, giftedWishInfo);
  return response.data;
};

export const deleteWish = async (wish: Wish): Promise<void> => {
  const response = await apiClient.delete<void>(`api/wish/${wish.id}`);
  return response.data;
};

export const uploadImage = async (image: File): Promise<Image> => {
  const formData = new FormData();
  formData.append('image', image);
  const response = await apiClient.post<Image>('api/v2/images', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  return response.data;
}

// ==========================================
// Secret Santa API Methods
// ==========================================

export const getSecretSantaGames = async (): Promise<SecretSantaGame[]> => {
  const response = await apiClient.get<SecretSantaGame[]>('api/v2/secret-santa/games');
  return response.data.map(game => ({
    ...game,
    event_date: game.event_date ? new Date(game.event_date) : null,
    created_at: new Date(game.created_at),
    updated_at: new Date(game.updated_at),
    participants: game.participants?.map(p => ({
      ...p,
      joined_at: new Date(p.joined_at)
    }))
  }));
};

export const getSecretSantaGame = async (id: string): Promise<SecretSantaGame> => {
  const response = await apiClient.get<SecretSantaGame>(`api/v2/secret-santa/games/${id}`);
  const game = response.data;
  return {
    ...game,
    event_date: game.event_date ? new Date(game.event_date) : null,
    created_at: new Date(game.created_at),
    updated_at: new Date(game.updated_at),
    participants: game.participants?.map(p => ({
      ...p,
      joined_at: new Date(p.joined_at)
    }))
  };
};

export const createSecretSantaGame = async (gameInfo: SecretSantaGameInfo): Promise<SecretSantaGame> => {
  const response = await apiClient.post<SecretSantaGame>('api/v2/secret-santa/games', gameInfo);
  const game = response.data;
  return {
    ...game,
    event_date: game.event_date ? new Date(game.event_date) : null,
    created_at: new Date(game.created_at),
    updated_at: new Date(game.updated_at),
    participants: game.participants?.map(p => ({
      ...p,
      joined_at: new Date(p.joined_at)
    }))
  };
};

export const updateSecretSantaGame = async (id: string, gameInfo: SecretSantaGameInfo): Promise<SecretSantaGame> => {
  const response = await apiClient.put<SecretSantaGame>(`api/v2/secret-santa/games/${id}`, gameInfo);
  const game = response.data;
  return {
    ...game,
    event_date: game.event_date ? new Date(game.event_date) : null,
    created_at: new Date(game.created_at),
    updated_at: new Date(game.updated_at),
    participants: game.participants?.map(p => ({
      ...p,
      joined_at: new Date(p.joined_at)
    }))
  };
};

export const joinSecretSantaGame = async (id: string, participantInfo: ParticipantInfo): Promise<SecretSantaParticipant> => {
  const response = await apiClient.post<SecretSantaParticipant>(`api/v2/secret-santa/games/${id}/join`, participantInfo);
  const participant = response.data;
  return {
    ...participant,
    joined_at: new Date(participant.joined_at)
  };
};

export const updateParticipant = async (id: string, participantInfo: ParticipantInfo): Promise<SecretSantaParticipant> => {
  const response = await apiClient.put<SecretSantaParticipant>(`api/v2/secret-santa/games/${id}/participant`, participantInfo);
  const participant = response.data;
  return {
    ...participant,
    joined_at: new Date(participant.joined_at)
  };
};

export const leaveSecretSantaGame = async (id: string): Promise<void> => {
  const response = await apiClient.post<void>(`api/v2/secret-santa/games/${id}/leave`);
  return response.data;
};

export const kickParticipant = async (id: string, participantId: string): Promise<void> => {
  const response = await apiClient.delete<void>(`api/v2/secret-santa/games/${id}/participants/${participantId}`);
  return response.data;
};

export const startDraw = async (id: string): Promise<{ game: SecretSantaGame, participants: SecretSantaParticipant[] }> => {
  const response = await apiClient.post<{ game: SecretSantaGame, participants: SecretSantaParticipant[] }>(`api/v2/secret-santa/games/${id}/draw`);
  const data = response.data;
  return {
    game: {
      ...data.game,
      event_date: data.game.event_date ? new Date(data.game.event_date) : null,
      created_at: new Date(data.game.created_at),
      updated_at: new Date(data.game.updated_at),
      participants: data.game.participants.map(p => ({
        ...p,
        joined_at: new Date(p.joined_at)
      }))
    },
    participants: data.participants?.map(p => ({
      ...p,
      joined_at: new Date(p.joined_at)
    }))
  };
};

export const getGameMessages = async (id: string): Promise<SecretSantaMessage[]> => {
  const response = await apiClient.get<SecretSantaMessage[]>(`api/v2/secret-santa/games/${id}/messages`);
  return response.data.map(message => ({
    ...message,
    sent_at: new Date(message.sent_at)
  }));
};

export const sendGameMessage = async (id: string, content: string): Promise<SecretSantaMessage> => {
  const response = await apiClient.post<SecretSantaMessage>(`api/v2/secret-santa/games/${id}/messages`, { content });
  const message = response.data;
  return {
    ...message,
    sent_at: new Date(message.sent_at)
  };
};
